/**
 * Out-of-home bids sent as zip codes (Bid Desk, 2026-10-14). Pure.
 *
 * A screen list with latitude and longitude is counted by exposure: who passed
 * each screen (counts/snowflake-ooh.ts). A list of zip codes has no screen to
 * pass, so it is counted the way the Bid Desk prices a regular iOOH bid: the
 * panelists living within a few miles of each zip, a tighter radius in New
 * York City, who used the app in the panel's login window. The radii are rate
 * settings (`ooh_resident_radius_miles`, `_nyc`).
 */

/**
 * The first three digits of New York City's zip codes: Manhattan (100–102),
 * Staten Island (103), the Bronx (104), Queens (111, 113, 114, 116) and
 * Brooklyn (112). 110 is left out: most of it is Nassau County.
 */
const NYC_ZIP_PREFIXES = new Set([
    '100',
    '101',
    '102',
    '103',
    '104',
    '111',
    '112',
    '113',
    '114',
    '116',
]);

export function isNycZip(code: string): boolean {
    return NYC_ZIP_PREFIXES.has(code.slice(0, 3));
}

/** A market of a zip list (its Market, DMA or City column), with its own completes when set. */
export interface ZipMarket {
    name: string;
    zips: string[];
    target?: number;
}

export interface ZipResidents {
    zips: string[];
    radiusMiles: number;
    nycRadiusMiles: number;
    /** The list's markets, each counted and estimated on its own; absent for one list. */
    markets?: ZipMarket[];
    /** How many locations (latitude and longitude) the zips were looked up from; absent for a zip list. */
    fromLocations?: number;
    /**
     * Who counts: the people living near the zips (`home`, the default) or the
     * people seen near them during the window (`seen`, counts/snowflake-zip-area.ts).
     */
    basis?: 'home' | 'seen';
}

/** Most markets one zip list is split into: each is one or two panel counts. */
export const MAX_ZIP_MARKETS = 30;

/** The name of the group for zips the list gives no market. */
export const OTHER_ZIPS_MARKET = 'Other zip codes';

/** The two markets of a list counted at its own New York City radius (M4S-5905). */
export const NYC_MARKET = 'New York City';
export const OUTSIDE_NYC_MARKET = 'Outside New York City';

/**
 * The markets a zip list is counted by, one row each the way the Bid Desk
 * answers a bid: the list's own markets, and its zips with no market as one
 * more. A list with no markets of its own whose New York City zips are counted
 * at a different radius is two markets, New York City and the rest, because
 * the Bid Desk quotes them apart (M4S-5905). Null for fewer than two, which is
 * the list itself.
 */
export function residentMarkets(r: ZipResidents): ZipMarket[] | null {
    if (!r.markets?.length) {
        if (r.nycRadiusMiles === r.radiusMiles) return null;
        const nyc = r.zips.filter(isNycZip);
        const rest = r.zips.filter((z) => !isNycZip(z));
        return nyc.length && rest.length
            ? [
                  { name: NYC_MARKET, zips: nyc },
                  { name: OUTSIDE_NYC_MARKET, zips: rest },
              ]
            : null;
    }
    const named = new Set(r.markets.flatMap((m) => m.zips));
    const rest = r.zips.filter((z) => !named.has(z));
    const all = [...r.markets, ...(rest.length ? [{ name: OTHER_ZIPS_MARKET, zips: rest }] : [])];
    return all.length > 1 ? all : null;
}

/** The zips each radius applies to: New York City's, then the rest. Empty groups are left out. */
export function zipRadiusGroups(
    r: ZipResidents,
): { codes: string[]; radiusMiles: number; nyc: boolean }[] {
    const nyc = r.zips.filter(isNycZip);
    const other = r.zips.filter((z) => !isNycZip(z));
    // One radius for everything: one count.
    if (r.nycRadiusMiles === r.radiusMiles)
        return [{ codes: r.zips, radiusMiles: r.radiusMiles, nyc: false }];
    return [
        ...(nyc.length ? [{ codes: nyc, radiusMiles: r.nycRadiusMiles, nyc: true }] : []),
        ...(other.length ? [{ codes: other, radiusMiles: r.radiusMiles, nyc: false }] : []),
    ];
}

const ZIP_HEADER = /^(zip|zip ?code|zipcode|postal ?code|zcta)$/i;

/**
 * The zip codes in a sheet's zip column (a header named zip, zip code, postal
 * code or ZCTA), with a leading zero a spreadsheet dropped put back. Null when
 * the sheet has no such column: five-digit numbers elsewhere are not zips.
 */
export function zipColumnCodes(rows: string[][]): string[] | null {
    const header = rows.findIndex((r) => r.some((c) => ZIP_HEADER.test(String(c ?? '').trim())));
    if (header < 0) return null;
    const col = rows[header].findIndex((c) => ZIP_HEADER.test(String(c ?? '').trim()));
    const codes = rows
        .slice(header + 1)
        .map((r) =>
            String(r[col] ?? '')
                .trim()
                .replace(/-\d{4}$/, ''),
        )
        .filter((c) => /^\d{3,5}$/.test(c))
        .map((c) => c.padStart(5, '0'));
    return [...new Set(codes)];
}

const MARKET_HEADER = /^(market|market name|dma|dma name|city|metro|msa|region|area)$/i;

/**
 * The zip list's markets, from a Market, DMA, City, Metro or Region column
 * beside its zip column, in the order the sheet first names them. A zip with a
 * blank market is left to "Other zip codes"; a zip named under two markets
 * stays with the first. Null when the sheet has no such column.
 */
export function zipColumnMarkets(rows: string[][]): ZipMarket[] | null {
    const cell = (c: unknown) => String(c ?? '').trim();
    const header = rows.findIndex((r) => r.some((c) => ZIP_HEADER.test(cell(c))));
    if (header < 0) return null;
    const zipCol = rows[header].findIndex((c) => ZIP_HEADER.test(cell(c)));
    const marketCol = rows[header].findIndex((c) => MARKET_HEADER.test(cell(c)));
    if (marketCol < 0) return null;
    const byName = new Map<string, ZipMarket>();
    const seen = new Set<string>();
    for (const r of rows.slice(header + 1)) {
        const code = cell(r[zipCol]).replace(/-\d{4}$/, '');
        const name = cell(r[marketCol]).replace(/\s+/g, ' ').slice(0, 60);
        if (!/^\d{3,5}$/.test(code) || !name) continue;
        const zip = code.padStart(5, '0');
        if (seen.has(zip)) continue;
        seen.add(zip);
        const key = name.toLowerCase();
        if (!byName.has(key)) byName.set(key, { name, zips: [] });
        byName.get(key)!.zips.push(zip);
    }
    return byName.size ? [...byName.values()] : null;
}

/**
 * A request that asks for the people living near its locations rather than
 * those who pass them: "residents within 15 miles", "people who live near".
 */
export function asksForResidents(text: string): boolean {
    return /\b(resident|residents|people who live|living (near|within|around)|live (near|within|around)|home (zip|location|address))/i.test(
        text,
    );
}

/**
 * The people living near a zip are not all pushed the survey: only those who
 * pass a placement during the flight are (location-triggered fielding). The
 * share pushed is measured from past out-of-home studies
 * (`ooh_resident_push_share`, refresh/derivations.ts), by flight length and by
 * how wide the radius is, since a wider radius holds more people who never pass.
 */
export const FLIGHT_BANDS: { key: string; maxDays: number }[] = [
    { key: 'upto_21d', maxDays: 21 },
    { key: '22_42d', maxDays: 42 },
    { key: '43_70d', maxDays: 70 },
    { key: '71_120d', maxDays: 120 },
    { key: 'over_120d', maxDays: Number.POSITIVE_INFINITY },
];

export function flightBand(days: number): string {
    return FLIGHT_BANDS.find((b) => days <= b.maxDays)!.key;
}

/**
 * The two radii the share is measured at outside New York City (3 miles inside
 * it), and which a check's radius reads: up to `NEAR_MAX_MILES` is near.
 */
export const RADIUS_BANDS = { near: 5, wide: 15 } as const;
export const NEAR_MAX_MILES = 7;
export const NYC_MEASURED_MILES = 3;

export function radiusBand(radiusMiles: number): keyof typeof RADIUS_BANDS {
    return radiusMiles <= NEAR_MAX_MILES ? 'near' : 'wide';
}

/**
 * A request that asks for the people who were in the area rather than those
 * who live there: "people seen in these zips", "visited the area", "traveled
 * through". Asking for residents wins when a request says both.
 */
export function asksForSeen(text: string): boolean {
    return (
        !asksForResidents(text) &&
        /\b(seen|been|were|was|visited|visiting|traveled|travelled|passed|passing|commut\w*)\s+((in|near|around|through|to|by)\s+)?(the|these|those|each)?\s*(area|zip|neighbou?rhood|market)/i.test(
            text,
        )
    );
}

/** Several lists' markets as one: a market named twice keeps the first name's spelling, and a zip stays with the first market that names it. */
export function mergeZipMarkets(lists: ZipMarket[][]): ZipMarket[] {
    const byName = new Map<string, ZipMarket>();
    const seen = new Set<string>();
    for (const m of lists.flat()) {
        const key = m.name.toLowerCase();
        if (!byName.has(key)) byName.set(key, { name: m.name, zips: [] });
        for (const z of m.zips)
            if (!seen.has(z)) {
                seen.add(z);
                byName.get(key)!.zips.push(z);
            }
    }
    return [...byName.values()].filter((m) => m.zips.length);
}

/** The markets a zip-code request carries: two or more, within the most one check counts. */
export function marketsForRequest(markets: ZipMarket[], zips: string[]): ZipMarket[] | undefined {
    const listed = new Set(zips);
    const kept = markets
        .map((m) => ({ name: m.name, zips: m.zips.filter((z) => listed.has(z)) }))
        .filter((m) => m.zips.length);
    return kept.length > 1 && kept.length <= MAX_ZIP_MARKETS ? kept : undefined;
}

/** The two default radii from the rate card, or null when either is not set. */
export function residentRadiusDefaults(
    resolve: (key: string) => number | null,
): { radiusMiles: number; nycRadiusMiles: number } | null {
    const radiusMiles = resolve('ooh_resident_radius_miles');
    const nycRadiusMiles = resolve('ooh_resident_radius_miles_nyc');
    return radiusMiles && nycRadiusMiles ? { radiusMiles, nycRadiusMiles } : null;
}

export interface StatedRadii {
    radiusMiles?: number;
    nycRadiusMiles?: number;
    /** The words they came from, for "where each detail came from". */
    passage?: string;
}

const MILES = String.raw`(\d{1,3})(?:\.\d)?\s*-?\s*(?:miles|mile|mi\b)`;
const NYC = String.raw`(?:NYC|NY\b|New\s+York(?:\s+City)?|Manhattan|the\s+five\s+boroughs)`;
const OTHER = String.raw`(?:all\s+)?(?:other|the\s+other|the\s+rest|everywhere\s+else|elsewhere|non[-\s]?NY\w*|outside\s+(?:of\s+)?(?:NYC|NY|New\s+York))`;
/** Words between a radius and what it is for: never across another number. */
const GAP = String.raw`[^.\n;\d]{0,40}?`;
/** The same, and never "outside": "15 miles outside NYC" is not New York City's radius. */
const NYC_GAP = String.raw`(?:(?!outside)[^.\n;\d]){0,40}?`;
const SHORT_GAP = String.raw`[^.\n;\d]{0,25}?`;

/**
 * The radii a request writes out (Bid Desk, 2026-10-14): "3 miles radius for
 * NY, 15 miles radius for other locations", "NYC 2 miles", "within 10 miles"
 * (one radius for every zip). A radius named for New York City and one for
 * the rest are read apart; an unqualified one applies to both.
 */
export function zipRadiiIn(text: string): StatedRadii {
    const out: StatedRadii = {};
    const passages: string[] = [];
    const take = (re: RegExp, key: 'radiusMiles' | 'nycRadiusMiles') => {
        if (out[key] != null) return;
        const m = re.exec(text);
        if (!m) return;
        const miles = Number(m[1]);
        if (miles >= 1 && miles <= 100) {
            out[key] = miles;
            passages.push(m[0].trim());
        }
    };
    // The rest first: "outside NYC" names New York, and is not its radius.
    take(new RegExp(`${MILES}${GAP}\\b(?:for|in|around|within)?\\s*${OTHER}`, 'i'), 'radiusMiles');
    take(new RegExp(`${OTHER}${SHORT_GAP}${MILES}`, 'i'), 'radiusMiles');
    take(
        new RegExp(`${MILES}${NYC_GAP}\\b(?:for|in|around|within)\\s+(?:the\\s+)?${NYC}`, 'i'),
        'nycRadiusMiles',
    );
    take(new RegExp(`(?<!outside\\s(?:of\\s)?)${NYC}${SHORT_GAP}${MILES}`, 'i'), 'nycRadiusMiles');
    if (out.radiusMiles == null && out.nycRadiusMiles == null) {
        const m = new RegExp(`${MILES}(?:\\s+radius)?`, 'i').exec(text);
        const miles = m ? Number(m[1]) : NaN;
        if (miles >= 1 && miles <= 100) {
            out.radiusMiles = miles;
            out.nycRadiusMiles = miles;
            passages.push(m![0].trim());
        }
    }
    if (passages.length) out.passage = [...new Set(passages)].join('; ').slice(0, 300);
    return out;
}

/**
 * The radii a zip-code bid uses: what the person wrote, then the one radius the
 * model read, then the rate card's defaults. Null when none of them gives both.
 */
export function zipRadii(
    stated: StatedRadii,
    modelMiles: number | null | undefined,
    defaults: { radiusMiles: number; nycRadiusMiles: number } | null,
): { radiusMiles: number; nycRadiusMiles: number; from: 'stated' | 'default' } | null {
    const model =
        modelMiles && modelMiles >= 1 && modelMiles <= 100 ? Math.round(modelMiles) : null;
    const radiusMiles = stated.radiusMiles ?? model ?? defaults?.radiusMiles;
    // New York City keeps its own (tighter) default unless one is written for it;
    // with no default at all, the one radius is everyone's.
    const nycRadiusMiles = stated.nycRadiusMiles ?? defaults?.nycRadiusMiles ?? radiusMiles;
    if (radiusMiles == null || nycRadiusMiles == null) return null;
    const statedAny = stated.radiusMiles != null || stated.nycRadiusMiles != null || model != null;
    return { radiusMiles, nycRadiusMiles, from: statedAny ? 'stated' : 'default' };
}

const n = (x: number) => x.toLocaleString('en-US');
const zipWord = (k: number) => `${n(k)} zip ${k === 1 ? 'code' : 'codes'}`;

/** "living within 5 miles of 120 zip codes (3 miles for the 14 in New York City)". */
export function describeResidents(r: ZipResidents): string {
    const from = r.fromLocations
        ? ` (where the ${n(r.fromLocations)} ${r.fromLocations === 1 ? 'location is' : 'locations are'})`
        : '';
    return (r.basis === 'seen' ? `seen ${describeZipArea(r)}` : describeZipGroups(r)) + from;
}

function describeZipGroups(r: ZipResidents, lead = 'living within'): string {
    const groups = zipRadiusGroups(r);
    const nyc = groups.find((g) => g.nyc);
    const rest = groups.find((g) => !g.nyc);
    if (nyc && rest)
        return `${lead} ${rest.radiusMiles} miles of ${zipWord(rest.codes.length)}, or ${nyc.radiusMiles} miles of ${zipWord(nyc.codes.length)} in New York City`;
    const only = nyc ?? rest!;
    return `${lead} ${only.radiusMiles} miles of ${zipWord(only.codes.length)}${nyc ? ' in New York City' : ''}`;
}

/** "within 15 miles of 120 zip codes": where a zip list's people were seen (counts/snowflake-zip-area.ts). */
export function describeZipArea(r: ZipResidents): string {
    return describeZipGroups(r, 'within');
}
